/**
 * « Nouvelle mission » (PLAN-BUREAU lot 5, mock-up « Nouvelle mission »): what Gilles hands Otto,
 * turned into what the server needs — a task title and the mission's objective, which names the
 * client and the procedure he picked so Otto starts from them. Pure.
 */

/** The Edge refuses an objective under 8 characters (work-agent-threads `start`). */
export const NEW_MISSION_MIN = 8;

/** …and over 4000 (`objective: z.string().trim().min(8).max(4000)`, work-agent-threads `start`). */
export const NEW_MISSION_MAX = 4000;

const TITLE_MAX = 90;

/** The task's title: the request's first line, cut on a word. */
export function newMissionTitle(text: string): string {
  const first = text.trim().split(/\r?\n/u)[0]?.trim() ?? '';
  if (first.length <= TITLE_MAX) return first;
  return `${first.slice(0, TITLE_MAX).replace(/\s+\S*$/u, '')}…`;
}

/** The mission's objective: the request as written, then the deal, the contact, the client and the procedure chosen. */
export function newMissionObjective(input: {
  text: string;
  account: { id: string; name: string } | null;
  contact?: { id: string; name: string } | null;
  opportunity?: { id: string; name: string } | null;
  procedure: { slug: string; label: string } | null;
}): string {
  const lines = [input.text.trim()];
  if (input.opportunity) lines.push(`Opportunité : ${input.opportunity.name} (opportunity_id: ${input.opportunity.id}).`);
  if (input.contact) lines.push(`Contact : ${input.contact.name} (contact_id: ${input.contact.id}).`);
  if (input.account) lines.push(`Client : ${input.account.name} (account_id: ${input.account.id}).`);
  if (input.procedure) lines.push(`Procédure à suivre : « ${input.procedure.label} » (compétence ${input.procedure.slug}).`);
  return lines.join('\n\n');
}

/** The client a « Nouvelle mission » link comes with: an account id, its name read by the pane. */
export const NEW_MISSION_CLIENT_PARAM = 'client';
/** The contact it comes with (a contact's sheet): the mission is tied to it, by its id. */
export const NEW_MISSION_CONTACT_PARAM = 'contact';
/** The deal it comes with (an opportunity's sheet): the mission is tied to it, by its id. */
export const NEW_MISSION_OPPORTUNITY_PARAM = 'opportunite';

/**
 * « Nouvelle mission » opened from elsewhere (the Équipe: « Lui confier une mission », Recruter; an
 * account's peek; the search), the request already written, a procedure or a client already
 * chosen; Gilles reads, edits, confides.
 */
export function newMissionHref(
  prefill: { text?: string; competence?: string | null; client?: string | null; contact?: string | null; opportunity?: string | null } = {},
): string {
  // Kept as written (a trailing space after « : » leaves the cursor ready to type).
  const params = new URLSearchParams({ nouvelle: prefill.text?.trim() ? prefill.text : '1' });
  if (prefill.competence) params.set('competence', prefill.competence);
  if (prefill.client) params.set(NEW_MISSION_CLIENT_PARAM, prefill.client);
  if (prefill.contact) params.set(NEW_MISSION_CONTACT_PARAM, prefill.contact);
  if (prefill.opportunity) params.set(NEW_MISSION_OPPORTUNITY_PARAM, prefill.opportunity);
  return `/admin/travail?${params.toString()}`;
}

/** The request a « Nouvelle mission » link carries (« 1 » only opens the pane). */
export const newMissionPrefill = (value: string | null): string => (value && value !== '1' ? value : '');

/**
 * « Ctrl+Entrée : en faire une mission » in the search (mock-up « Recherche »): the account picked
 * becomes the mission's client; a contact or an opportunity picked comes by its id — the mission is
 * tied to it —, its account as the client; with nothing picked, the words typed are the request.
 */
export function searchMissionHref(
  query: string,
  pick: { kind: string; id: string; title: string; accountId: string | null } | null,
): string {
  if (pick?.kind === 'crm_account') return newMissionHref({ client: pick.id });
  // Tied by its key (its chip in the pane), not named in the request (audit du 13/10/2026).
  if (pick?.kind === 'crm_contact') return newMissionHref({ contact: pick.id, client: pick.accountId });
  if (pick?.kind === 'crm_opportunity') return newMissionHref({ opportunity: pick.id, client: pick.accountId });
  return newMissionHref({ text: query.trim() });
}
